<template>
  <div class="scenery">
    <router-link :to="{ name: 'chrs' }">
      <img src="/img/Logo.png" />
    </router-link>
    <div class="stars">
      <div v-for="i in count" :key="i" class="star"></div>
    </div>
    <div class="title">
      <h1>Payment Success</h1>
    </div>
    <div class="message">
      <h2>Thank you for your payment</h2>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { mapState } from 'vuex';
import { getItem, removeItem } from '@/store/localStorage';
import { post } from '@/api/Services';

// Reactive state
const sessionId = getItem('sessionId');
const jobId = ref(null);
const jobData = getItem('JobData');
const user = ref({
  sqlId: 28,
  parameters: [],
});

// Define methods as functions
const writeDB = async () => {
  try {
    const { loggeduser } = useStore().state.account;
    await post('/Payments/payment-success', {
      session_id: sessionId,
      user_id: loggeduser.id,
      job_id: jobId.value,
      company_id: jobData.NewJob.Company.id,
      payment_status_id: 1,
    });
    removeItem('JobData');
    removeItem('sessionId');
    useRouter().push({ name: 'jobOpenings' });
  } catch (err) {
    console.error('Error during DB write:', err);
  }
};

const newPost = async () => {
  const parameters = [
    // Add parameters here, similar to how you structured them before
    { parameter_name: 'pin_job_id', parameter_value: -1 },
    {
      parameter_name: 'piv_reference_code',
      parameter_value: jobData.NewJob?.RefCode || '',
    },
    // Repeat for other fields...
  ];

  user.value.parameters = JSON.stringify(parameters);

  try {
    const response = await post('Commons/Executer', user.value);
    if (response && response[0].status === 0) {
      jobId.value = response[0].job_opening_id;
      // Handle success logic (such as recruiting)
      await handleRecruiters(response[0].job_opening_id);
    } else {
      useToast().add({
        severity: 'error',
        summary: 'Error',
        detail: response[0].message,
        life: 3000,
      });
    }
  } catch (err) {
    useToast().add({
      severity: 'error',
      summary: 'Error',
      detail: err.message,
      life: 3000,
    });
  }
};

const handleRecruiters = async (jobId) => {
  if (jobData.NewJob.CompanyResponse) {
    for (const item of jobData.NewJob.CompanyResponse) {
      await sendRecruiterData(jobId, item.responsible_user_id, 'Company');
    }
  }
  if (jobData.NewJob.CrowdHrResponse) {
    for (const item of jobData.NewJob.CrowdHrResponse) {
      await sendRecruiterData(jobId, item.responsible_user_id, 'CrowdHr');
    }
  }
};

const sendRecruiterData = async (jobId, responsibleUserId, type) => {
  const userData = { sqlId: type === 'Company' ? 32 : 33 };
  const parameters = [
    { parameter_name: 'pin_job_id', parameter_value: jobId || -1 },
    {
      parameter_name: 'pin_responsible_user_id',
      parameter_value: responsibleUserId,
    },
    { parameter_name: 'piv_command_type', parameter_value: 'I' },
  ];

  userData.parameters = JSON.stringify(parameters);

  try {
    await post('Commons/Executer', userData);
  } catch (err) {
    useToast().add({
      severity: 'error',
      summary: 'Error',
      detail: err.message,
      life: 3000,
    });
  }
};

// Run the necessary API calls once the component is mounted
onMounted(async () => {
  await newPost();
  await writeDB();
});

// Computed property for account state
const account = computed(() => useStore().state.account);
</script>

<style lang="scss" scoped>
$count: 100;
$shadow: #fc6c2a;
$light: #fedbae;
$gradient1: #4bb543;
$gradient2: #003e75;
$squareSize: 150px;

// mixins.scss

// Gradient Background Mixin
@mixin background-image($gradient) {
  background: $gradient;
  background: -webkit-linear-gradient($gradient);
  background: -moz-linear-gradient($gradient);
  background: -o-linear-gradient($gradient);
  background: -ms-linear-gradient($gradient);
}

// Box Shadow Mixin
@mixin box-shadow($shadow) {
  -webkit-box-shadow: $shadow;
  -moz-box-shadow: $shadow;
  -ms-box-shadow: $shadow;
  box-shadow: $shadow;
}

// Border Radius Mixin
@mixin border-radius($radius) {
  -webkit-border-radius: $radius;
  -moz-border-radius: $radius;
  -ms-border-radius: $radius;
  border-radius: $radius;
}

// Transition Mixin
@mixin transition($properties) {
  -webkit-transition: $properties;
  -moz-transition: $properties;
  -o-transition: $properties;
  transition: $properties;
}

// Transform Mixin
@mixin transform($transforms) {
  -webkit-transform: $transforms;
  -moz-transform: $transforms;
  -ms-transform: $transforms;
  transform: $transforms;
}

@keyframes shining {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0.2;
  }
}

body {
  background-color: #482472;
}

//---------------------------
// GENERAL SCENERY
//---------------------------
.zero {
  @include background-image(linear-gradient($gradient2, #fc6c2a));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.scenery {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
  overflow: hidden;
  border-radius: 5px;
  color: #fff;
  @include background-image(linear-gradient($gradient1, $gradient2));
  @include box-shadow(rgba(0, 0, 0, 0.8) 0px 0px 100px);
}

//---------------------------
// STARS
//---------------------------
.star {
  position: absolute;
  z-index: 1;
  background-color: #fff;
  animation-name: shining;
  animation-timing-function: ease;
  animation-direction: alternate;
  animation-iteration-count: infinite;
  @include box-shadow(#fff 0px 0px 5px);
  @include border-radius(200px);
}

@for $i from 1 to $count {
  $size: random(3) + 0px;

  .star:nth-child(#{$i}) {
    width: $size;
    height: $size;
    bottom: random(618) + 0px;
    left: random(1000) + 0px;
    animation-duration: random(4) / random(3) + 0s;
  }

  //---------------------------
  // TEXT
  //---------------------------
}

.title {
  position: relative;
  z-index: 50;

  h1 {
    font-size: 8rem;
    font-weight: bold;
    display: inline-block;
    background: linear-gradient(0deg, #fff, #4bb543);
    background-size: 200% 200%;
    /* Arka plan boyutunu ayarlayın */
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: gradientText 3s ease infinite;
  }
}

@keyframes gradientText {
  0% {
    background-position: 50% 0%;
  }

  50% {
    background-position: 50% 60%;
  }

  100% {
    background-position: 50% 0%;
  }
}

.message {
  margin-top: 25px;

  h2 {
    font-size: 1.8rem;
  }
}

.action {
  margin-top: 30px;

  button {
    padding: 8px 16px;
    font-size: 1.2rem;
    color: #fff;
    border: 2px solid $shadow;
    background-color: $shadow;
    border-radius: 50px;
    @include transition(all 0.2s ease);
    box-shadow: 0px 0px 15px $shadow;

    &:hover {
      cursor: pointer;
      background-color: transparent;
    }

    //---------------------------
    // FIGURE
    //---------------------------
  }
}

.square {
  width: $squareSize;
  height: $squareSize;
  display: inline-block;
  position: relative;
  vertical-align: middle;
  left: -30px;
  @include transform(scale(1.1) rotate(45deg));

  > * {
    position: absolute;
  }

  .light {
    box-shadow: 0px 0px 10px $light;
    background-color: $light;
  }

  .light1,
  .light3 {
    width: $squareSize / 10;
    height: $squareSize - $squareSize / 10;
    @include transform(skew(0deg, 45deg));
  }

  .light2,
  .light4 {
    width: $squareSize - $squareSize / 5;
    height: $squareSize / 10;
    @include transform(skew(45deg));
  }

  .light1 {
    z-index: 10;
    top: $squareSize / 20 - 2px;
    left: $squareSize / 10;
  }

  .light3 {
    z-index: 2;
    top: $squareSize / 20;
    right: $squareSize / 10;
  }

  .light2 {
    top: 0;
    left: $squareSize / 10 + ($squareSize / 10)/2;
  }

  .light4 {
    bottom: 0;
    right: $squareSize / 10 + ($squareSize / 10)/2;
  }

  .shadow {
    background-color: $shadow;
    box-shadow: 0px 0px 10px $shadow;
  }

  .shadow1 {
    bottom: $squareSize / 20;
    right: 0;
    width: $squareSize / 10;
    height: $squareSize - $squareSize / 5;
    @include transform(skew(0deg, -45deg));
  }

  .shadow2 {
    z-index: 3;
    top: $squareSize / 10;
    left: $squareSize / 10 + ($squareSize / 10)/2;
    width: $squareSize - $squareSize / 5;
    height: $squareSize / 10;
    @include transform(skew(-45deg));
  }

  .stairs1,
  .stairs2,
  .stairs3,
  .stairs4 {
    li {
      position: relative;
      width: $squareSize/20;
      height: $squareSize/20;
      background-color: $light;
      box-shadow: 0px 0px 10px $light;
      @include transform(rotate(45deg));
    }
  }

  .stairs1,
  .stairs2 {
    li {
      &::before {
        content: '';
        position: absolute;
        left: 0;
        width: $squareSize/20;
        height: 13px;
        @include background-image(linear-gradient(left, $light, $shadow));
        box-shadow: 0px 0px 10px $light;
      }
    }
  }

  .stairs1 {
    top: 1px;
    left: 11px;
    width: 0px;
    height: $squareSize;

    li {
      margin-bottom: 3px;

      &::before {
        top: 100%;
      }
    }
  }

  .stairs2 {
    z-index: 10;
    bottom: 19px;
    left: 3px;
    width: $squareSize;
    height: 0px;
    display: flex;

    li {
      margin-right: 3.5px;

      &:last-child {
        &::before {
          background: $light;
        }
      }

      &::before {
        bottom: 100%;
      }
    }
  }

  .stairs3 {
    bottom: -32px;
    right: 34px;
    width: 0px;
    height: $squareSize;
    z-index: 10;

    li {
      margin-bottom: 3.5px;
    }
  }

  .stairs4 {
    top: -4px;
    left: 16px;
    width: $squareSize;
    height: 0px;
    display: flex;

    li {
      margin-right: 3.5px;
    }

    //---------------------------
    // PLANETS
    //---------------------------
  }
}
</style>
